"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronUp, List } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TocEntry } from "@/components/blog/TableOfContents";

/**
 * Mobiles Inhaltsverzeichnis (< lg, dort ist die Sidebar ausgeblendet):
 * eingeklappte Leiste am unteren Bildschirmrand, klappt per Tap nach oben auf.
 */
export default function MobileToc({ entries }: { entries: TocEntry[] }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(entries[0]?.id ?? "");

  // Scrollspy – gleiche Logik wie im Desktop-Inhaltsverzeichnis
  useEffect(() => {
    const headings = entries
      .map((entry) => document.getElementById(entry.id))
      .filter((el): el is HTMLElement => el !== null);
    if (!headings.length) return;

    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -65% 0px", threshold: 0 }
    );
    headings.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [entries]);

  function scrollTo(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${id}`);
    setActiveId(id);
    setOpen(false);
  }

  if (!entries.length) return null;

  return (
    <div className="lg:hidden fixed inset-x-4 bottom-4 z-40 mx-auto max-w-md flex flex-col gap-2">
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            aria-label="Inhaltsverzeichnis"
            className="rounded-2xl border border-line bg-paper shadow-xl p-2.5 max-h-[55vh] overflow-y-auto"
          >
            <ul className="flex flex-col gap-0.5">
              {entries.map((entry, i) => {
                const active = activeId === entry.id;
                return (
                  <li key={entry.id}>
                    <a
                      href={`#${entry.id}`}
                      onClick={(e) => scrollTo(e, entry.id)}
                      className={cn(
                        "group flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm leading-snug transition-colors duration-150",
                        active
                          ? "bg-accent-soft text-accent font-semibold"
                          : "text-ink-soft hover:text-ink hover:bg-paper-deep"
                      )}
                    >
                      <span
                        className={cn(
                          "tnum shrink-0 text-xs font-bold mt-0.5",
                          active ? "text-accent" : "text-ink-faint"
                        )}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {entry.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Inhaltsverzeichnis schließen" : "Inhaltsverzeichnis öffnen"}
        className="flex items-center justify-between gap-3 rounded-full border border-line bg-paper shadow-lg px-5 py-3 text-sm font-semibold text-ink cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <List size={15} aria-hidden="true" />
          Inhalt
        </span>
        <ChevronUp
          size={16}
          aria-hidden="true"
          className={cn(
            "text-ink-faint transition-transform duration-300",
            open && "rotate-180"
          )}
        />
      </button>
    </div>
  );
}
